/**
 * _icons.ts
 * -----------------------------------------------------------------------------
 * Central mapping from legacy FontAwesome icon names (used by the original
 * Bootstrap procurement screens) to their lucide-react equivalents. Lets us
 * port FA-flavoured JSX into React without sprinkling per-page lookups.
 *
 * Usage:
 *   import { Icon, Fa } from './_icons';
 *
 *   <Icon.eye size={14} />              // direct lookup
 *   <Fa name="paper-plane" size={14} /> // helper component (string-driven)
 *
 * Keys are the FA glyph names with the `fa-` prefix stripped — i.e. callers
 * pass `paper-plane`, not `fa-paper-plane`. The <Fa /> helper also accepts
 * the legacy `fa-…` form and strips it for convenience.
 */

import {
  Anchor,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  BadgeCheck,
  Barcode,
  BarChart3,
  Bell,
  BookCheck,
  Box,
  Bot,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  Clock,
  Cog,
  FileArchive,
  FolderOpen,
  Gavel,
  Globe,
  Handshake,
  Landmark,
  MailOpen,
  MessagesSquare,
  Receipt,
  Satellite as SatelliteIcon,
  ShieldCheck,
  TestTube,
  Check,
  CheckCheck,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Download,
  ExternalLink,
  Eye,
  Factory,
  FastForward,
  File as FileIcon,
  FileCheck,
  FileSearch,
  FileInput,
  FileOutput,
  FileSignature,
  FileText,
  FileUp,
  GanttChartSquare,
  Gauge,
  GitBranch,
  History,
  Hourglass,
  Info,
  Keyboard,
  Layers,
  LayoutGrid,
  LayoutList,
  Loader2,
  Lock,
  MapPin,
  PackageOpen,
  PieChart,
  Pencil,
  Plus,
  RotateCcw,
  Satellite,
  Scale,
  Search,
  Send,
  Settings,
  Ship,
  SortAsc,
  SortDesc,
  Stamp,
  Target,
  Timer,
  Trash2,
  Trophy,
  Truck,
  Container as ContainerIcon,
  Sparkles,
  Upload,
  User,
  Users,
  Warehouse,
  Waves,
  Wind,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import { createElement } from 'react';
import type { ComponentType, ReactElement, SVGProps } from 'react';

/** Lucide icon component signature (forwardRef components accept these props). */
export type LucideIcon = ComponentType<
  SVGProps<SVGSVGElement> & { size?: number | string }
>;

/**
 * FA name → lucide component map. Keys are FA glyph names with the `fa-`
 * prefix stripped, e.g. `paper-plane` (not `fa-paper-plane`).
 *
 * Usage in JSX: `<Icon.eye size={14} />`
 */
export const Icon = {
  'paper-plane': Send,
  'eye': Eye,
  'keyboard': Keyboard,
  'check-circle': CheckCircle2,
  'scale-balanced': Scale,
  'file-signature': FileSignature,
  'file-upload': FileUp,
  'file-invoice-dollar': FileText,
  'file-export': FileOutput,
  'hourglass-half': Hourglass,
  'redo': RotateCcw,
  'stopwatch': Timer,
  'box-open': PackageOpen,
  'barcode': Barcode,
  'ship': Ship,
  'broadcast-tower': Send,
  'anchor': Anchor,
  'bell': Bell,
  'satellite-dish': Satellite,
  'search': Search,
  'fast-forward': FastForward,
  'exclamation-triangle': AlertTriangle,
  'check-double': CheckCheck,
  'check': Check,
  'clipboard-check': ClipboardCheck,
  'edit': Pencil,
  'trash': Trash2,
  'plus': Plus,
  'times': X,
  'download': Download,
  'lock': Lock,
  'trophy': Trophy,
  'history': History,
  'chart-bar': BarChart3,
  'tachometer-alt': Gauge,
  'bullseye': Target,
  'user': User,
  'users': Users,
  'building': Building2,
  'arrow-left': ArrowLeft,
  'cube': Box,
  'cog': Settings,
  'info-circle': Info,
  'exclamation-circle': AlertCircle,
  // Approvals-page glyphs
  'truck': Truck,
  'stamp': Stamp,
  'upload': Upload,
  'file': FileIcon,
  'times-circle': XCircle,
  'chevron-down': ChevronDown,
  'up-right-from-square': ExternalLink,
  'spinner': Loader2,
  // Import-Queue extra glyphs (Workspace header + Kanban + Timeline pipeline)
  'list-ul': LayoutList,
  'columns': LayoutGrid,
  'stream': GanttChartSquare,
  'layer-group': Layers,
  'wind': Wind,
  'boxes': Boxes,
  'map-marker-alt': MapPin,
  'sort-amount-down': SortDesc,
  'sort-amount-up': SortAsc,
  'arrow-right': ArrowRight,
  'file-medical': FileSearch,
  'file-import': FileInput,
  'file-contract': FileCheck,
  'file-invoice': FileText,
  'check-square': CheckSquare,
  'chart-pie': PieChart,
  'industry': Factory,
  'box': Box,
  'water': Waves,
  'warehouse': Warehouse,
  'truck-loading': Truck,
  'clipboard-list': ClipboardList,
  'money-check-alt': BadgeCheck,
  'user-clock': User,
  'git-branch': GitBranch,
  'briefcase': Briefcase,
  'book-check': BookCheck,
  'wrench': Wrench,
  // Control Tower modal glyphs
  'satellite': SatelliteIcon,
  'cubes': Boxes,
  'cogs': Cog,
  'comments-dollar': MessagesSquare,
  'university': Landmark,
  'gavel': Gavel,
  'shield-alt': ShieldCheck,
  'handshake': Handshake,
  'receipt': Receipt,
  'certificate': Award,
  'file-archive': FileArchive,
  'vial': TestTube,
  'file-alt': FileText,
  'file-pdf': FileText,
  'globe': Globe,
  'clock': Clock,
  // Porta-Cabin smart-assist glyphs
  'robot': Bot,
  'calculator': Calculator,
  'magic': Sparkles,
  'container': ContainerIcon,
  'folder-open': FolderOpen,
  'envelope-open-text': MailOpen,
  'search-dollar': Search,
} as const satisfies Record<string, LucideIcon>;

export type FaIconName = keyof typeof Icon;

interface FaProps {
  /**
   * FA glyph name. Accepts either the bare key (`paper-plane`) or the legacy
   * dotted-prefix form (`fa-paper-plane`) — the leading `fa-` is stripped.
   */
  name: FaIconName | `fa-${FaIconName}` | string;
  size?: number | string;
  className?: string;
}

/**
 * String-driven helper — useful for porting existing `<i class="fas fa-…" />`
 * markup where the icon name is data-driven. Falls back to rendering nothing
 * if the lookup misses, so callers don't have to guard.
 *
 * Written with `createElement` instead of JSX so this file can stay a `.ts`
 * (matching the spec) without flipping tsconfig.
 */
export function Fa({ name, size = 14, className }: FaProps): ReactElement | null {
  const key = (name.startsWith('fa-') ? name.slice(3) : name) as FaIconName;
  const Cmp = (Icon as Record<string, LucideIcon | undefined>)[key];
  if (!Cmp) return null;
  return createElement(Cmp, { size, className });
}
